import * as Sentry from '@sentry/react';
import {
  useFetchers,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  useLoaderData,
  useRevalidator,
} from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import supabase from '@/utils/supabase';

import OverviewTab from './OverviewTab';
import DetailsTab from './DetailsTab';
import VideosTab from './VideosTab';
import MediaTab from './MediaTab';
import ClientTab from './ClientTab';
import { useEffect } from 'react';

import StatusBadge from '@/components/StatusBadge';
import {
  getProjectTeamStatus,
  getTeamStatusDisplay,
  optimisticTeamStatus,
} from '@/utils/status';
import { getAuthHeaders } from '@/utils/auth';

import type { Database } from '@/types/database.types';
type Deliverable = Database['public']['Tables']['deliverables']['Row'];
type DeliverableTeam = Database['public']['Tables']['deliverables_team']['Row'];
export type DeliverableWithTeam = Deliverable & {
  is_promoting: boolean;
  is_ingesting: boolean;
  deliverables_team: DeliverableTeam | null;
};

export type ActionResult =
  | {
      intent: 'teamApprove';
      result:
        | 'started'
        | 'in_progress'
        | 'source_processing'
        | 'team_processing'
        | 'team_reupload'
        | 'unknown'
        | 'failed';
    }
  | { intent: 'teamNeedsRevision'; result: 'sent' | 'failed' };

export type ApproveResult = Extract<
  ActionResult,
  { intent: 'teamApprove' }
>['result'];

export async function loader({ params }: LoaderFunctionArgs) {
  const { projectId } = params;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  if (!projectId || !userId) {
    throw new Response('Unauthorised', { status: 401 });
  }

  const [projectResult, deliverableResult, uploadsResult, membershipResult] =
    await Promise.all([
      supabase
        .from('projects')
        .select('*, projects_team(*)')
        .eq('id', projectId)
        .single(),
      supabase
        .from('deliverables')
        .select('*, is_promoting, is_ingesting, deliverables_team(*)')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true })
        .overrideTypes<DeliverableWithTeam[], { merge: false }>(),
      supabase
        .from('uploads')
        .select()
        .eq('project_id', projectId)
        .eq('upload_type', 'media')
        .order('created_at', { ascending: true }),
      supabase
        .from('project_members')
        .select('project_role')
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .single(),
    ]);

  const { data: project, error: projectError } = projectResult;
  const { data: deliverables, error: deliverablesError } = deliverableResult;
  const { data: uploads, error: uploadsError } = uploadsResult;
  const { data: membership, error: membershipError } = membershipResult;

  if (projectError || !project) {
    throw new Response('Failed to load project', { status: 500 });
  }

  if (deliverablesError || !deliverables) {
    throw new Response('Failed to load deliverables', { status: 500 });
  }

  if (uploadsError || !uploads) {
    throw new Response('Failed to load uploads', { status: 500 });
  }

  if (membershipError || !membership) {
    throw new Response('Failed to load membership', { status: 500 });
  }

  const projectRole = membership.project_role;

  if (project.status === 'pending' && projectRole === 'editor') {
    const [deliverablesUpdate, deliverablesTeamUpdate] = await Promise.all([
      supabase
        .from('deliverables')
        .update({ status: 'in_progress' })
        .eq('project_id', projectId)
        .eq('status', 'pending'),
      supabase
        .from('deliverables_team')
        .update({ team_status: 'in_progress' })
        .in(
          'deliverable_id',
          deliverables.map((d) => d.id)
        )
        .eq('team_status', 'pending')
        .select(),
    ]);

    if (deliverablesUpdate.error || deliverablesTeamUpdate.error) {
      console.error(
        'Failed to auto-progress statuses',
        deliverablesUpdate.error,
        deliverablesTeamUpdate.error
      );
    }

    deliverables.forEach((d) => {
      if (d.status === 'pending') d.status = 'in_progress';
      if (d.deliverables_team?.team_status === 'pending') {
        d.deliverables_team.team_status = 'in_progress';
      }
    });
  }

  return { project, deliverables, uploads, projectRole };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { projectId } = params;
  const formData = await request.formData();
  const intent = formData.get('intent') as string;
  const deliverableId = formData.get('deliverableId') as string;

  if (!deliverableId) {
    throw new Response('Missing deliverable Id', { status: 400 });
  }

  if (intent === 'teamNeedsRevision') {
    const { error } = await supabase
      .from('deliverables_team')
      .update({ team_status: 'editor_revision' })
      .eq('deliverable_id', deliverableId);

    if (error) {
      console.error(error);
      return Response.json({
        intent: 'teamNeedsRevision',
        result: 'failed',
      } satisfies ActionResult);
    }
    return Response.json({
      intent: 'teamNeedsRevision',
      result: 'sent',
    } satisfies ActionResult);
  }

  if (intent === 'teamApprove') {
    try {
      const response = await fetch(
        `/api/team/projects/${projectId}/deliverables/${deliverableId}/approve`,
        {
          method: 'POST',
          headers: await getAuthHeaders(),
        }
      );
      if (response.ok) {
        return Response.json({
          intent: 'teamApprove',
          result: 'started',
        } satisfies ActionResult);
      }
      if (response.status === 409) {
        const body = await response.json().catch(() => null);
        let result: ApproveResult;

        switch (body?.code) {
          case 'parent_ingest_in_progress':
            result = 'source_processing';
            break;
          case 'team_ingest_processing':
            result = 'team_processing';
            break;
          case 'team_reupload_required':
            result = 'team_reupload';
            break;
          default:
            result = 'in_progress';
        }

        return Response.json({
          intent: 'teamApprove',
          result,
        } satisfies ActionResult);
      }
      if (response.status === 524) {
        return Response.json({
          intent: 'teamApprove',
          result: 'unknown',
        } satisfies ActionResult);
      }
      return Response.json({
        intent: 'teamApprove',
        result: 'failed',
      } satisfies ActionResult);
    } catch (error) {
      console.error(error);
      return Response.json({
        intent: 'teamApprove',
        result: 'unknown',
      } satisfies ActionResult);
    }
  }
  throw new Response('Unknown intent', { status: 400 });
}

export default function TeamProjectDetails() {
  const revalidator = useRevalidator();
  const fetchers = useFetchers();
  const { project, deliverables, uploads, projectRole } =
    useLoaderData<typeof loader>();

  useEffect(() => {
    if (
      !project.frame_io_project_id ||
      !project.frame_io_client_folder_id ||
      !project.frame_io_client_review_link_id ||
      !project.projects_team?.frame_io_team_folder_id ||
      !project.projects_team?.frame_io_team_review_link_id
    ) {
      (async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          const response = await fetch(
            `/api/team/projects/${project.id}/setup-frameio`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
              },
            }
          );
          if (!response.ok) {
            let body: unknown;
            try {
              body = await response.json();
            } catch {
              body = await response.text().catch(() => '<unreadable>');
            }
            const error = new Error(
              `Frame.io setup request failed: ${response.status} ${response.statusText}`
            );
            Sentry.captureException(error, {
              tags: { component: 'frameio', op: 'setup-trigger' },
              extra: {
                projectId: project.id,
                status: response.status,
                statusText: response.statusText,
                body,
              },
            });
            console.error('frameio setup failed:', response.status, body);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'frameio', op: 'setup-trigger' },
            extra: { projectId: project.id, kind: 'network' },
          });
          console.error('Frame.io setup network error:', error);
        }
      })();
    }
  }, [
    project.id,
    project.frame_io_project_id,
    project.frame_io_client_folder_id,
    project.frame_io_client_review_link_id,
    project.projects_team?.frame_io_team_folder_id,
    project.projects_team?.frame_io_team_review_link_id,
  ]);

  useEffect(() => {
    const channelName = `deliverables_team_changes_${Date.now()}`;

    const setupChannel = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      supabase.realtime.setAuth(session?.access_token ?? null);

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'deliverables_team',
          },
          () => {
            revalidator.revalidate();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'deliverables',
          },
          () => {
            revalidator.revalidate();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'ingest_attempts',
          },
          () => {
            revalidator.revalidate();
          }
        )
        .subscribe();
      return channel;
    };

    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | undefined;
    setupChannel().then((ch) => {
      if (cancelled) {
        supabase.removeChannel(ch);
        return;
      }
      channel = ch;
    });
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const optimistic = new Map<string, string>();
  for (const f of fetchers) {
    const id = f.formData?.get('deliverableId');
    if (!id) continue;
    const status = optimisticTeamStatus(f.formData);
    if (status) optimistic.set(String(id), status);
  }

  const projectStatusCode = getProjectTeamStatus(
    deliverables.flatMap((d: DeliverableWithTeam) => {
      const status =
        optimistic.get(d.id) ?? d.deliverables_team?.team_status ?? 'error';
      return d.is_ingesting ? ['processing', status] : [status];
    }),
    projectRole
  );

  const { label, colour, pulse } = getTeamStatusDisplay(
    projectStatusCode,
    projectRole
  );

  return (
    <>
      <header>
        <div className="flex flex-row gap-4 items-center mb-5">
          <StatusBadge label={label} colour={colour} pulse={pulse} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <h1 className="text-3xl font-bold">{project.project_name}</h1>
        </div>
      </header>
      <div className="grid">
        <div className="max-w-300">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList variant={'line'} className="mb-4">
              <TabsTrigger value="overview" className="px-6 cursor-pointer">
                Overview
              </TabsTrigger>
              <TabsTrigger value="details" className="px-6 cursor-pointer">
                Details
              </TabsTrigger>
              <TabsTrigger value="videos" className="px-6 cursor-pointer">
                Videos
              </TabsTrigger>
              <TabsTrigger value="media" className="px-6 cursor-pointer">
                Media
              </TabsTrigger>
              <TabsTrigger value="client" className="px-6 cursor-pointer">
                Client
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>
            <TabsContent value="details">
              <DetailsTab project={project} />
            </TabsContent>
            <TabsContent value="videos">
              <VideosTab
                deliverables={deliverables}
                project={project}
                projectRole={projectRole}
              />
            </TabsContent>
            <TabsContent value="media">
              <MediaTab uploads={uploads} project={project} />
            </TabsContent>
            <TabsContent value="client">
              <ClientTab />
            </TabsContent>
          </Tabs>
        </div>
        {/* <div className="col-span-1 sticky top-0 self-start">
          <ChatPanel projectId={project.id} />
        </div> */}
      </div>
    </>
  );
}
